/** Shelf tags, work-order helpers, and token cost used by the operating loop. */

export const SHELF_TAG_BASE = [
  "code",
  "property",
  "financial",
  "personal",
  "ministry",
  "operations",
  "general",
] as const;

export type ShelfTagBase = (typeof SHELF_TAG_BASE)[number];

export const SHELF_EVAL_QUESTIONS = [
  "What did we decide about pricing?",
  "Where is the shelf brief stored?",
  "What is the library note about?",
  "Which client is named in the note?",
  "What tags belong on a general note?",
  "What should staff do with a field guide?",
  "What is the acceptance criteria for this request?",
  "Which source document supports the answer?",
  "What constraint applies to client data?",
  "What is the goal of the work order?",
] as const;

const INPUT_PER_MILLION: Record<string, number> = {
  "openai/gpt-4o-mini": 0.15,
  "anthropic/claude-sonnet-4": 3,
  "anthropic/claude-3.5-sonnet": 3,
};
const OUTPUT_PER_MILLION: Record<string, number> = {
  "openai/gpt-4o-mini": 0.6,
  "anthropic/claude-sonnet-4": 15,
  "anthropic/claude-3.5-sonnet": 15,
};

export function isShelfTag(tag: string): boolean {
  const value = tag.trim();
  if ((SHELF_TAG_BASE as readonly string[]).includes(value)) return true;
  return /^client:[a-z0-9][a-z0-9-]{0,40}$/i.test(value);
}

/** Untagged input becomes general. Unknown tags are dropped. Empty becomes general. */
export function normalizeShelfTags(tags: string[] | null | undefined): string[] {
  const kept = (tags ?? []).map((tag) => tag.trim()).filter((tag) => tag && isShelfTag(tag));
  const unique = [...new Set(kept)];
  return unique.length > 0 ? unique : ["general"];
}

export function tagNeedsReadAudit(tags: string[]): boolean {
  return tags.some((tag) => tag === "financial" || tag === "personal" || tag.startsWith("client:"));
}

export function needsSlackApproval(tags: string[], sensitivity: string): boolean {
  if (sensitivity === "client" || sensitivity === "restricted") return true;
  return tagNeedsReadAudit(tags);
}

export function estimateCallCostUsd(input: {
  model: string;
  inputTokens: number;
  outputTokens: number;
  cachedTokens?: number;
}): number {
  const model = input.model.trim();
  const inputRate = INPUT_PER_MILLION[model] ?? (model.includes("mini") || model.includes("flash") ? 0.15 : 3);
  const outputRate = OUTPUT_PER_MILLION[model] ?? (model.includes("mini") || model.includes("flash") ? 0.6 : 15);
  const cached = Math.max(0, input.cachedTokens ?? 0);
  const freshInput = Math.max(0, input.inputTokens - cached);
  const usd = (freshInput * inputRate + cached * inputRate * 0.1 + Math.max(0, input.outputTokens) * outputRate) / 1_000_000;
  return Math.round(usd * 1_000_000) / 1_000_000;
}

export function chunkPlainText(text: string, size = 2000): string[] {
  const clean = text.replace(/\s+/g, " ").trim();
  if (!clean) return [];
  const chunks: string[] = [];
  for (let i = 0; i < clean.length; i += size) chunks.push(clean.slice(i, i + size));
  return chunks;
}

export function defaultBudgetTokens(tier: "fast" | "strong" | "private"): number {
  return tier === "fast" ? 800 : 4000;
}
